<template>
    <div v-if="layoutVersion === 'v1'" class="cielo-layout cielo-layout--v1">
        <CieloHeader />
        <div class="cielo-layout__container">
            <section class="cielo-layout__section cielo-layout__section--slot" ref="startingPoint">
                <slot />
            </section>
        </div>
    </div>

    <div v-else-if="layoutVersion === 'v2'" class="cielo-layout cielo-layout--v2">
        <CieloHeader />

        <div class="cielo-layout__container">
            <section class="cielo-layout__section cielo-layout__section--link-info">
                <LinkSummary
                    :is-mobile="renderMobile()"
                />
                <CieloFooter v-if="!renderMobile()" />
            </section>
            <section class="cielo-layout__section cielo-layout__section--slot" ref="startingPoint">
                <!-- bbpay countdown -->
                <Countdown v-if="isBBPayPayment" :seconds="900" @finished="handleCountdownFinished" />
                <slot />
            </section>
            <PaymentTimeoutModal
                v-if="showTimeoutModal"
                @close="showTimeoutModal = false"
            />
            <CieloFooter v-if="renderMobile()" />
        </div>
        <ReloadCountdownModal
            v-if="showReloadModal"
        />
        <LogoutModal
            v-if="showLogoutModal"
        />
        <DccLoadingOverlay
            v-if="isDccLoading"
        />
        <!--
            A expiração pode acontecer depois que a tela onde a cotação foi feita (CardData)
            já foi desmontada — o comprador pode estar em outra etapa quando o timer de
            validade dispara. Fica aqui, e não dentro de CurrencyChoice, por isso. Depende de
            toda etapa do pipeline anterior ao pagamento continuar usando layoutVersion="v2"
            (hoje é o caso — "v1" é só pós-pagamento/erro, onde o DCC já deixou de importar).
        -->
        <DccExpiredModal
            v-if="expiredNotice"
            @new-quote="requestNewQuote"
            @close="dismissExpiredNotice"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isMobile } from '@/helpers/isMobile';
import { logException } from '@/helpers/cieloLogger';

import CieloHeader from '@/components/template/CieloHeader';
import CieloFooter from '@/components/template/CieloFooter';
import ReloadCountdownModal from '@/views/DataOnFile/components/ReloadCountdownModal';
import LogoutModal from '@/views/DataOnFile/components/LogoutModal.vue';
import Countdown from '@/components/layout/Countdown.vue';
import PaymentTimeoutModal from '@/components/template/PaymentTimeoutModal.vue';
import LinkSummary from '@/components/LinkSummary';
import DccLoadingOverlay from '@/components/Dcc/DccLoadingOverlay.vue';
import DccExpiredModal from '@/components/Dcc/DccExpiredModal.vue';
import bbPayFetcher from '@/services/bbPayFetcher';
import {
    resolveDccExpiredAction,
    resolveDccCloseRoute,
    DccExpiredActions,
    CARD_DATA_ROUTE,
    DOF_REVIEW_ROUTE
} from '@/helpers/dccExpiredAction';

export default {
  components: {
    CieloHeader,
    CieloFooter,
    ReloadCountdownModal,
    LogoutModal,
	  Countdown,
    LinkSummary,
    PaymentTimeoutModal,
    DccLoadingOverlay,
    DccExpiredModal
  },

  data() {
	return {
		showTimeoutModal: false
	};
  },

  computed: {
    ...mapGetters({
      showReloadModal: 'dataOnFile/getShowReloadModal',
      showLogoutModal: 'dataOnFile/getShowLogoutModal',
      isBBPayPayment: 'bBPayPayment/getIsBBPayPayment',
      isDccLoading: 'dcc/getIsLoading',
      expiredNotice: 'dcc/getExpiredNotice',
    }),
  },

  methods: {
    renderMobile() {
      return isMobile('1024');
    },
    /**
     * O que "nova cotação" faz depende de onde o comprador está (ver dccExpiredAction). O
     * aviso é lido antes de ser dispensado, porque é ele quem diz se veio da finalização.
     */
    requestNewQuote() {
        const action = resolveDccExpiredAction({
            duringFinalization: !!(this.expiredNotice && this.expiredNotice.duringFinalization),
            routeName: this.$route.name
        });

        if (action === DccExpiredActions.RELOAD) return window.location.reload();

        this.$store.dispatch('dcc/dismissExpiredNotice');

        if (action === DccExpiredActions.GO_TO_CARD_DATA) {
            this.$store.dispatch('pipeline/pushTo', CARD_DATA_ROUTE);
        } else if (action === DccExpiredActions.GO_TO_DOF_REVIEW) {
            this.$store.dispatch('pipeline/pushTo', DOF_REVIEW_ROUTE);
        }

        // Sem os dados obrigatórios válidos não há o que simular: só fecha o aviso, e o
        // plugin de invalidação pede a cotação sozinho quando eles ficarem válidos.
        if (this.$store.getters['dcc/getIsSelectable']) {
            this.$store.dispatch('dcc/requestQuote', { isRetryAfterExpiry: true });
        }
    },
    /**
     * Só fechar o modal não refaz a cotação, mas leva o comprador de volta à tela de dados
     * do cartão ou ao Review do DoF, conforme o fluxo (ver resolveDccCloseRoute).
     */
    dismissExpiredNotice() {
        const route = resolveDccCloseRoute(this.$route.name);

        this.$store.dispatch('dcc/dismissExpiredNotice');

        if (route) this.$store.dispatch('pipeline/pushTo', route);
    },
    applyStartPoint() {
        try {
          setTimeout(() => {
              let element = this.$refs.startingPoint;
              if(!element) return;
              let firstChild = element.querySelector(':first-child').querySelector(':first-child');
			        if(!firstChild) return;
              firstChild.classList.add('starting-point');
              firstChild.focus();
          }, 1000);
        } catch (error) {
            logException(error);
        }
    },
	async handleCountdownFinished() {
        this.showTimeoutModal = true;
        try {
            const preorderId = this.$route.query.id;
            const response = await bbPayFetcher.abandonPreorder(preorderId);

            this.$store.dispatch('pipeline/pushToOrderStatus', response.data.Data.OrderId);
        } catch (error) {}
	},
  },

  props: {
    showMerchantName: {
      type: Boolean,
      default: true
    },
    layoutVersion: {
      type: String,
      default: 'v2'
    },
    startingPointBySlot: {
      type: Boolean,
      default: true
    }
  },

  mounted() {
      if(this.startingPointBySlot) this.applyStartPoint();
  }
};
</script>

<style lang="scss" scoped>
.cielo-layout{
    display: flex;
    flex-direction: column;
    min-height: 100%;

    @media screen and (min-width: 1025px) {
      justify-content: center;
    }

  &--v1 {
      .cielo-header {
          border-bottom: 1px solid #B8C1CC;
      }

      .cielo-layout {
          &__container {
              display: flex;
              flex-direction: column;
              justify-content: center;
              flex: 1 0 auto;
              width: 100%;

              @media screen and (min-width: 1025px) {
                max-width: 1180px;
                margin: 0 auto;
                padding: 48px 16px;
                flex-direction: row;
              }

              @media screen and (min-width: 1165px) {
                padding: 48px 0;
              }
          }

          &__section--slot {
              display: flex;
              flex-direction: column;
              flex: 1 0 auto;
              width: 100%;
              padding: 24px 16px;

              @media screen and (min-width: 1025px) {
                  padding: 0;
              }
          }
      }
  }

  &--v2 {
      .cielo-layout {
          &__container {
              display: flex;
              flex-direction: column;
              align-self: center;
              flex: 1 0 auto;
              width: 100%;

              @media screen and (min-width: 1025px) {
                max-width: 1180px;
                margin: 0 auto;
                padding: 48px 16px;
                flex-direction: row;
              }

              @media screen and (min-width: 1165px) {
                padding: 48px 0;
              }
          }

          &__section {}

          &__section--link-info {
              @media screen and (min-width: 1025px) {
                  order: 2;
                  margin-left: 60px;
                  width: calc(45% - 60px);
              }
          }

          &__section--slot {
              padding: 24px 16px;

              @media screen and (max-width: 1024px) {
                  display: flex;
                  flex-direction: column;
                  flex: 1 0 auto;
              }


              @media screen and (min-width: 1025px) {
                  padding: 0;
                  margin-right: 60px;
                  width: calc(55% - 60px);
              }
          }
      }
  }

}
</style>
